"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Layers, ShieldCheck, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyCapabilities = [{
  title: "Multi-Channel Fiber Optics",
  description: "Up to 6 multiplexed optical channels for simultaneous point monitoring across tanks and lines.",
  icon: Activity
}, {
  title: "ATEX & Hazardous Zone Rated",
  description: "Certified for direct integration into explosion-risk and GMP manufacturing environments.",
  icon: ShieldCheck
}, {
  title: "Permanently Aligned Optics",
  description: "RockSolid interferometer design eliminates drift and minimizes routine maintenance overhead.",
  icon: Cpu
}];
const technicalSpecs = [{
  label: "Spectral Range",
  value: "12,800 - 4,000 cm⁻¹"
}, {
  label: "Spectral Resolution",
  value: "< 2 cm⁻¹ standard"
}, {
  label: "Interface Standards",
  value: "OPC, Modbus, 4-20mA"
}, {
  label: "Compliance",
  value: "21 CFR Part 11 & GMP"
}];
export default function MatrixHero() {
  return <section id="matrixfhero" data-nav="light" className="relative overflow-hidden bg-background py-16 lg:py-24 border-b border-border">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Product Positioning & Action */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                Bruker Optics Platform
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs font-medium text-muted-foreground">
                Process Analytical Technology (PAT)
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight mb-6">
              MATRIX-F II Process FT-NIR Spectrometer
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8 font-sans">
              Direct in-line and on-line real-time analytical measurements for pharmaceutical, chemical, and food manufacturing. Delivers continuous monitoring of blend uniformity, reaction progress, and drying endpoints with verified laboratory-grade precision.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="h-12 px-6 shadow-sm">
                <Link to="/BookADemoPage" className="inline-flex items-center gap-2">
                  <span>Schedule Instrument Demo</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-6">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" />
                  <span>Discuss Application</span>
                </Link>
              </Button>
            </div>

            {/* Application Highlights */}
            <div className="w-full pt-6 border-t border-border">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Validated Industrial Use Cases
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="text-sm font-medium text-foreground">Blend Uniformity</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="text-sm font-medium text-foreground">Drying Endpoints</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="text-sm font-medium text-foreground">Raw Material ID</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Instrument Display & Technical Card */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8 overflow-hidden shadow-sm">
              {/* Decorative top accent badge */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Zap className="size-4 text-primary" />
                  <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    High-Throughput PAT Unit
                  </span>
                </div>
                <span className="text-xs font-mono text-muted-foreground bg-background px-2.5 py-1 rounded-md border border-border">
                  Model: MATRIX-F II
                </span>
              </div>

              {/* Instrument Graphic Container */}
              <div className="relative aspect-4/3 w-full rounded-xl bg-card border border-border flex items-center justify-center p-6 overflow-hidden mb-6 shadow-xs">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/matrix_f_spectrometer_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/matrix_f_spectrometer_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/matrix_f_spectrometer_hero.png-wvc-sizes" alt="MATRIX-F II industrial FT-NIR process analytical spectrometer" loading="lazy" decoding="async" className="w-full h-full object-contain object-center" />
              </div>

              {/* Specification Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="p-3 rounded-lg bg-background border border-border">
                    <div className="text-xs text-muted-foreground">{spec.label}</div>
                    <div className="text-xs sm:text-sm font-semibold text-foreground mt-0.5 font-mono">
                      {spec.value}
                    </div>
                  </div>)}
              </div>

              {/* Link to all NIR spectrometers */}
              <div className="pt-2">
                <Button asChild variant="ghost" size="sm" className="w-full justify-between text-foreground">
                  <Link to="/NirProcessSpectrometersPage" className="flex items-center justify-between w-full">
                    <span className="text-xs font-medium">Explore Full NIR Process Range</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

        </div>

        <Separator className="my-14" />

        {/* Bottom Feature Capabilities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyCapabilities.map((feature, i) => {
          const IconComponent = feature.icon;
          return <Card key={feature.title} data-index={i} className="border-border bg-card hover:border-border/80 transition-[border-color,box-shadow] shadow-xs">
                <CardContent className="pt-6">
                  <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-4">
                    <IconComponent className="size-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground font-default mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}